/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from "react";
import { SelectInput } from "components/Select";
import { useFilterActions } from "../../../ducks/filter/hooks/actions";
import { useFilterState } from "../../../ducks/filter/hooks/filterState";
import {
  useGetStoresOptionsQuery,
  useGetStoresResponsiblesOptionsQuery,
  useGetRegionalResponsibleQuery,
} from "@/services/store/select-options";
import { StatusEnumStoreConciliation } from "@/../src/enum/statusConciliation";
import { RoleEnum } from "@/enum/role";
import { StoreConciliationDivergenceValueRange } from "../../constants/divergence-ranges";
import { filterClasses, getSelectClasses } from "@/utils/filter-styles";
import { MultiValueTag } from "@/components/ui/MultiValueTag";
import { formatOptionLabel } from "@/utils/format-option-label";

const statusOptions = [
  {
    value: StatusEnumStoreConciliation.OPENED,
    label: "Aberto",
  },
  {
    value: StatusEnumStoreConciliation.CLOSED,
    label: "Fechado",
  },
  {
    value: StatusEnumStoreConciliation.PENDING,
    label: "Aguardando",
  },
  {
    value: StatusEnumStoreConciliation.NOT_INTEGRATED,
    label: "Não Integrado",
  },
];

const availableToCloseOptions = [
  {
    value: "true",
    label: "Sim",
  },
  {
    value: "false",
    label: "Não",
  },
];

export default function SelectFilters() {
  const { updateFilter } = useFilterActions();
  const { filters } = useFilterState();
  const isInitialized = useRef(false);

  const { data: storeOptions } = useGetStoresOptionsQuery(
    undefined,
    { refetchOnMountOrArgChange: false }
  );
  const { data: responsibleOptions } = useGetStoresResponsiblesOptionsQuery(
    { role: RoleEnum.ANALISTA },
    { refetchOnMountOrArgChange: false }
  );
  const { data: regionOptions } = useGetRegionalResponsibleQuery(
    undefined,
    { refetchOnMountOrArgChange: false }
  );

  const getStoredFilters = () => JSON.parse(localStorage.getItem("storeFilters") || "{}");
  const setStoredFilters = (filters: any) => localStorage.setItem("storeFilters", JSON.stringify(filters));

  useEffect(() => {
    if (!isInitialized.current) {
      const savedFilters = getStoredFilters();
      
      Object.entries(savedFilters).forEach(([key, value]) => {
        updateFilter({ key, value });
      });
      
      isInitialized.current = true;
    }
  }, [updateFilter]);

  const handleFilterChange = (key: string) => (option: any) => {
    const value = Array.isArray(option) 
      ? option.length > 0 ? option.map((opt) => opt.value) : undefined
      : option?.value;

    updateFilter({ key, value });
    updateFilter({ key: "meta", value: { page: 1 } });

    const currentFilters = getStoredFilters();
    setStoredFilters({ ...currentFilters, [key]: value });
  };

  const handleSingleFilterChange = (key: string) => (option: any) => {
    const value = option?.value || undefined;

    updateFilter({ key, value });
    updateFilter({ key: "meta", value: { page: 1 } });

    const currentFilters = getStoredFilters();
    setStoredFilters({ ...currentFilters, [key]: value });
  };

  const getCurrentSelectedOptions = (key: string, options: any[]) => {
    const filterValue = filters[key];
    if (!filterValue) return [];
    
    if (Array.isArray(filterValue)) {
      return options.filter(option => filterValue.includes(option.value));
    }
    
    const singleOption = options.find(option => option.value === filterValue);
    return singleOption ? [singleOption] : [];
  };

  const getCurrentSingleSelectedOption = (key: string, options: any[]) => {
    const filterValue = filters[key];
    if (!filterValue) return null;
    
    // Para filtros single, esperamos sempre um string, não array
    const singleOption = options.find(option => option.value === filterValue);
    return singleOption || null;
  };

  const filtersData = [
    {
      key: "code",
      options: storeOptions || [],
      placeholder: "Loja",
      value: getCurrentSelectedOptions("code", storeOptions || []),
      onChange: handleFilterChange("code"),
    },
    {
      key: "status",
      options: statusOptions,
      placeholder: "Situação",
      value: getCurrentSelectedOptions("status", statusOptions),
      onChange: handleFilterChange("status"),
    },
    {
      key: "regional_id",
      options: regionOptions || [],
      placeholder: "Regional",
      value: getCurrentSelectedOptions("regional_id", regionOptions || []),
      onChange: handleFilterChange("regional_id"),
    },
    {
      key: "responsible",
      options: responsibleOptions || [],
      placeholder: "Responsável",
      value: getCurrentSelectedOptions("responsible", responsibleOptions || []),
      onChange: handleFilterChange("responsible"),
    },
    {
      key: "divergence_value_range",
      options: StoreConciliationDivergenceValueRange,
      placeholder: "Faixa de Divergência",
      value: getCurrentSingleSelectedOption("divergence_value_range", StoreConciliationDivergenceValueRange),
      onChange: handleSingleFilterChange("divergence_value_range"),
      isMulti: false,
    },
    {
      key: "available_to_close",
      options: availableToCloseOptions,
      placeholder: "Pode Finalizar?",
      value: getCurrentSingleSelectedOption("available_to_close", availableToCloseOptions),
      onChange: handleSingleFilterChange("available_to_close"),
      isMulti: false,
    },
  ];

  return (
    <div className={filterClasses.container}>
      {filtersData.map((filter) => {
        const isMultiFilter = filter.isMulti !== false;
        const hasValue = isMultiFilter 
          ? filter.value.length > 0 
          : filter.value !== null && filter.value !== undefined;
        
        return (
          <div key={filter.placeholder} className={filterClasses.filterItem}>
            <SelectInput
              options={filter.options}
              placeholder={filter.placeholder}
              value={filter.value}
              onChange={filter.onChange}
              className={getSelectClasses(hasValue)}
              classNamePrefix="an-simple-select"
              isMulti={isMultiFilter}
              isClearable
              closeMenuOnSelect={!isMultiFilter}
              hideSelectedOptions={false}
              components={isMultiFilter ? { MultiValue: MultiValueTag } : {}}
              formatOptionLabel={formatOptionLabel}
            />
          </div>
        );
      })}
    </div>
  );
}
